import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ReferralBuiltPreview, type ReferralCheck } from '@/components/patient/ReferralBuiltPreview';
import { CheckCircle2, ChevronLeft, Download, File, MoreHorizontal, Send, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ReferralStatusChip } from '@/components/patient/ReferralStatusChip';
import { OutboxVansPanel, vansAttemptsQueryKey } from '@/components/patient/OutboxVansPanel';
import {
  useReferralOutbox,
  type OutboxData,
  type OutboxRow,
} from '@/components/patient/referralOutboxData';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { MenuPopover } from '@/components/ui/Popover';
import { useAuth } from '@/lib/auth';
import { ApiCallError, callFunction } from '@/lib/api';
import { canImportReceiptManually } from '@/lib/referral/manualReceiptImport';
import { applyImportedReceipt, type ImportedReceipt } from '@/lib/referral/importedReceipt';
import { downloadLatin1Edifact, downloadLatin1Xml } from '@/lib/referral/latin1Xml';
import {
  outboundReferralKindLabel,
  receiptHistoryKindLabel,
  refuseCodeLabel,
} from '@/lib/referral/outboundReferralLabels';
import { formatCourseDateTime } from '@/lib/courseDateFormat';
import {
  isOutboundReferralStatus,
  knownRefuseCode,
  type ReceiptHistoryItem,
} from '@/lib/referral/referralReceipt';
import {
  receiptImportErrorFeedback,
  receiptImportFeedback,
  type ReceiptImportFeedback,
  type ReceiptImportResult,
} from '@/lib/referral/receiptImportFeedback';
import { patientHenvisningPath } from '@/lib/patientRecordPaths';
import { Banner } from '@/components/ui/Banner';
import { FORM_ERROR_TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

type ImportResult = ReceiptImportResult & {
  id?: string;
  refuseCode?: string | null;
  companionExchangeId?: string;
  errorText?: string;
};

type PayloadResult = { xml?: string; edi?: string; checks?: ReferralCheck[]; ok?: boolean | null };

export function PatientOutboundReferralDetail({
  clinicId,
  patientId,
  outboxId,
}: {
  clinicId: string;
  patientId: string;
  outboxId: string;
}) {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const justCreated = searchParams.has('created');
  const { isPlatformAdmin, isClinicAdmin } = useAuth();
  const canImport = canImportReceiptManually({
    isPlatformAdmin,
    isClinicAdmin: isClinicAdmin(clinicId),
  });
  const queryClient = useQueryClient();
  const query = useReferralOutbox(clinicId, patientId);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const row = (query.data?.rows ?? []).find((item) => item.id === outboxId) ?? null;
  const listTo = patientHenvisningPath(patientId, { vis: 'sendte' });
  const receiverName = row?.receiver_organisation_name || row?.receiver_ean || '';
  const previewQuery = useQuery({
    queryKey: ['hospital-referral-preview', clinicId, row?.exchange_id],
    enabled: Boolean(row?.exchange_id),
    queryFn: async () => {
      const exchangeId = row?.exchange_id;
      if (!exchangeId) throw new Error('Missing exchange');
      const result = await callFunction<
        { action: 'payload'; clinicId: string; exchangeId: string; includeChecks: boolean },
        PayloadResult
      >('communicate-ref', {
        action: 'payload',
        clinicId,
        exchangeId,
        includeChecks: true,
      });
      if (result.xml === undefined) throw new Error('Missing XML payload');
      let edi = result.edi;
      if (row?.kind === 'XREF06' && edi === undefined) {
        try {
          const edifact = await callFunction<
            { action: 'payload'; clinicId: string; exchangeId: string; format: 'EDIFACT' },
            PayloadResult
          >('communicate-ref', {
            action: 'payload',
            clinicId,
            exchangeId,
            format: 'EDIFACT',
          });
          edi = edifact.edi;
        } catch {
          edi = undefined;
        }
      }
      const checks = result.checks ?? [];
      return {
        xml: result.xml,
        edi,
        checks,
        ok: checks.length > 0 ? result.ok !== false : null,
      };
    },
  });

  async function refresh() {
    await queryClient.invalidateQueries({
      queryKey: ['hospital-referral-outbox', clinicId, patientId],
    });
  }

  async function showImportedReceipt(rowId: string, receipt: ImportedReceipt) {
    const outboxKey = ['hospital-referral-outbox', clinicId, patientId];
    const apply = () =>
      queryClient.setQueriesData<OutboxData>({ queryKey: outboxKey }, (data) =>
        applyImportedReceipt(data, rowId, receipt),
      );
    apply();
    await refresh();
    apply();
  }

  async function onDownload(exchangeId: string, fileName: string) {
    setBusy(true);
    setError('');
    try {
      const result = await callFunction<
        { action: 'payload'; clinicId: string; exchangeId: string },
        PayloadResult
      >('communicate-ref', { action: 'payload', clinicId, exchangeId });
      if (result.xml === undefined) throw new Error('Missing XML payload');
      downloadLatin1Xml(result.xml, fileName);
    } catch (err) {
      setError(
        err instanceof ApiCallError
          ? err.message
          : t('patients.hospital_referral.outbox_download_failed', 'XML-filen kunne ikke hentes.'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function onDownloadEdifact(target: OutboxRow) {
    setBusy(true);
    setError('');
    try {
      const result = await callFunction<
        { action: 'payload'; clinicId: string; exchangeId: string; format: 'EDIFACT' },
        PayloadResult
      >('communicate-ref', {
        action: 'payload',
        clinicId,
        exchangeId: target.exchange_id,
        format: 'EDIFACT',
      });
      if (result.edi === undefined) throw new Error('Missing EDIFACT payload');
      downloadLatin1Edifact(result.edi, `REF06_${target.envelope_identifier}.edi`);
    } catch (err) {
      setError(
        err instanceof ApiCallError
          ? err.message
          : t('patients.hospital_referral.outbox_download_edifact_failed', 'EDIFACT-filen kunne ikke hentes.'),
      );
    } finally {
      setBusy(false);
    }
  }

  function report(feedback: ReceiptImportFeedback) {
    if (feedback.tone === 'error') {
      toast.error(feedback.message, { duration: 10_000 });
      setError(feedback.message);
    } else {
      toast.success(feedback.message, { duration: 6_000 });
    }
  }

  async function onImport(target: OutboxRow, file: File) {
    setBusy(true);
    setError('');
    try {
      const xml = new TextDecoder('iso-8859-1').decode(new Uint8Array(await file.arrayBuffer()));
      const result = await callFunction<
        { action: 'import'; clinicId: string; xml: string; referralId: string },
        ImportResult
      >('communicate-ctl', { action: 'import', clinicId, xml, referralId: target.id });
      if (result.importKind === 'vans-receipt') {
        await queryClient.invalidateQueries({ queryKey: vansAttemptsQueryKey(clinicId, target.id) });
        toast.success(
          t('patients.hospital_referral.receipt_import_vans', 'VANS-kvitteringen er indlæst på kuverten.'),
          { duration: 6_000 },
        );
        return;
      }
      if (result.importKind === 'vans-rejected') {
        report(
          receiptImportErrorFeedback(
            {
              message:
                result.errorText ||
                t('patients.hospital_referral.outbox_unreadable', 'Filen kunne ikke læses.'),
            },
            t,
          ),
        );
        return;
      }
      const companionFile = result.companionExchangeId
        ? target.companions.find((item) => item.exchangeId === result.companionExchangeId)?.fileName ?? null
        : null;
      const feedback = receiptImportFeedback(result, target, t, companionFile);
      report(feedback);
      if (
        feedback.refresh &&
        result.id &&
        result.status &&
        result.status !== 'built' &&
        (result.attachment === 'matched' || result.attachment === 'doublet')
      ) {
        await showImportedReceipt(target.id, {
          id: result.id,
          status: result.status,
          refuseCode: result.refuseCode ?? null,
          refuseText: result.refuseText ?? null,
          receivedAt: new Date().toISOString(),
        });
      } else if (feedback.refresh) await refresh();
    } catch (err) {
      report(receiptImportErrorFeedback(err, t));
    } finally {
      setBusy(false);
    }
  }

  if (query.isLoading && !query.data) {
    return (
      <Card aria-busy>
        <p className="hv-sub">{t('patients.hospital_referral.loading', 'Henter…')}</p>
      </Card>
    );
  }
  if (!row) {
    return (
      <Card>
        <Banner
          tone="error"
          title={t('patients.hospital_referral.detail_missing', 'Henvisningen blev ikke fundet')}
        >
          <Link to={listTo} className="font-semibold text-ds-teal">
            {t('patients.hospital_referral.back_to_sent', 'Sendte henvisninger')}
          </Link>
        </Banner>
      </Card>
    );
  }

  const status = isOutboundReferralStatus(row.status) ? row.status : null;
  const refuse = knownRefuseCode(row.refuse_code);
  const refuseText = query.data?.refuseTextById[row.id];
  const sentAt = formatCourseDateTime(row.created_at, i18n.language) || null;
  const receiptAt = formatCourseDateTime(query.data?.receiptReceivedAtById[row.id], i18n.language) || null;
  const negative = status === 'xctl01' || status === 'xctl02';
  const history = query.data?.receiptHistoryById[row.id] ?? [];
  const kind = outboundReferralKindLabel(row.kind, t);
  const receiptKind =
    status === 'xctl01'
      ? t('patients.hospital_referral.receipt_history_xctl01', 'Negativ VANS-kvittering (XCTL01)')
      : status === 'xctl02'
        ? t('patients.hospital_referral.receipt_history_xctl02', 'Negativ kvittering (XCTL02)')
        : status === 'xctl03'
          ? t('patients.hospital_referral.receipt_history_xctl03', 'Positiv kvittering (XCTL03)')
          : t('patients.hospital_referral.receipt_badge_waiting', 'Afventer kvittering');

  return (
    <Card>
      <nav className="hv-crumb" aria-label={t('common.breadcrumb', 'Brødkrumme')}>
        <Link to={listTo}>
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          {t('patients.hospital_referral.back_to_sent', 'Sendte henvisninger')}
        </Link>
        <span className="sep" aria-hidden>
          /
        </span>
        <span>{receiverName}</span>
      </nav>
      <div className="hv-dh">
        <div>
          <div className="hl">
            <h2>
              {t('patients.hospital_referral.detail_title', '{{kind}} til {{name}}', {
                kind,
                name: receiverName,
              })}
            </h2>
            <ReferralStatusChip status={row.status} />
          </div>
          {sentAt ? (
            <p className="hv-sub num">
              {t('patients.hospital_referral.outbox_sent_at', 'Afsendt')} {sentAt}
            </p>
          ) : null}
        </div>
        <div className="r">
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => void onDownload(row.exchange_id, `${row.kind}_${row.envelope_identifier}.xml`)}
          >
            <Download className="h-4 w-4" aria-hidden />
            {t('patients.hospital_referral.download', 'Hent XML')}
          </Button>
          {row.kind === 'XREF06' ? (
            <Button type="button" variant="secondary" disabled={busy} onClick={() => void onDownloadEdifact(row)}>
              <Download className="h-4 w-4" aria-hidden />
              {t('patients.hospital_referral.download_edifact', 'Hent EDIFACT (REF06)')}
            </Button>
          ) : null}
          {canImport ? (
            <MenuPopover
              align="end"
              trigger={
                <Button type="button" variant="secondary" size="icon" aria-label={t('patients.header.more_actions')}>
                  <MoreHorizontal />
                </Button>
              }
            >
              <label className="block cursor-pointer px-2.5 py-s2 text-[14px]">
                {t('patients.hospital_referral.outbox_import_admin', 'Indlæs kvittering (kun admin)')}
                <Input
                  type="file"
                  accept=".xml,application/xml,text/xml"
                  disabled={busy}
                  className="mt-s2"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) void onImport(row, file);
                  }}
                />
              </label>
            </MenuPopover>
          ) : null}
        </div>
      </div>

      {negative ? (
        <div className="hv-neg" role="status">
          <XCircle className="h-5 w-5" aria-hidden />
          <div>
            <b>
              {receiptKind}
              {refuse ? ` · ${refuseCodeLabel(refuse, t)}` : ''}
            </b>
            <p>
              {refuseText ||
                t(
                  'patients.hospital_referral.negative_notice',
                  'Henvisningen er afvist af det modtagende system. Årsagen er ikke nærmere angivet.',
                )}
            </p>
            {receiptAt ? (
              <p className="m num">
                {t('patients.hospital_referral.outbox_receipt_at', 'Kvittering modtaget')} {receiptAt}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {justCreated ? (
        <Banner
          tone="success"
          className="mt-s4"
          title={t('patients.hospital_referral.toast_built', 'Henvisningen er dannet.')}
        >
          {t(
            'patients.hospital_referral.result_not_sent',
            'Henvisningen er lagt i udbakken. Den er ikke sendt videre.',
          )}
        </Banner>
      ) : null}

      {error ? (
        <p role="alert" className={cn('mt-s4', FORM_ERROR_TYPE)}>
          {error}
        </p>
      ) : null}

      {previewQuery.isError ? (
        <p role="alert" className={cn('mt-s4', FORM_ERROR_TYPE)}>
          {t('patients.hospital_referral.outbox_download_failed', 'XML-filen kunne ikke hentes.')}
        </p>
      ) : null}

      {previewQuery.data ? (
        <ReferralBuiltPreview
          variant="detail"
          xml={previewQuery.data.xml}
          checks={previewQuery.data.checks}
          ok={previewQuery.data.ok}
          edi={previewQuery.data.edi}
          xmlFileName={`${row.kind}_${row.envelope_identifier}.xml`}
          ediFileName={row.kind === 'XREF06' ? `REF06_${row.envelope_identifier}.edi` : undefined}
          showOutboxNote={!justCreated}
        />
      ) : previewQuery.isLoading ? (
        <div className="hv-box">
          <h3>{t('patients.hospital_referral.checks', 'Kontrol')}</h3>
          <div className="hv-panel">
            <p className="hv-sub">{t('patients.hospital_referral.loading', 'Henter…')}</p>
          </div>
        </div>
      ) : null}

      <div className="hv-box">
        <h3>{t('patients.hospital_referral.shipment', 'Forsendelse')}</h3>
        <div className="hv-panel">
          <dl className="hv-kv">
            <Fact label={t('patients.hospital_referral.receiver_section', 'Modtager')} value={receiverName} />
            <Fact label={t('patients.hospital_referral.ean', 'EAN')} value={row.receiver_ean} />
            <Fact label={t('patients.hospital_referral.outbox_kind', 'Henvisningstype')} value={kind} />
            <Fact
              label={t('patients.hospital_referral.outbox_refuse', 'Afvisningskode')}
              value={refuse ? refuseCodeLabel(refuse, t) : t('patients.hospital_referral.empty', 'Ikke udfyldt')}
              muted={!refuse}
            />
            <Fact label={t('patients.hospital_referral.outbox_envelope', 'Kuvert-nummer')} value={row.envelope_identifier} />
            <Fact label={t('patients.hospital_referral.outbox_letter', 'Brev-nummer')} value={row.letter_identifier} />
            <Fact
              label={t('patients.hospital_referral.outbox_sent_at', 'Afsendt')}
              value={sentAt ?? t('patients.hospital_referral.empty', 'Ikke udfyldt')}
            />
            <Fact
              label={t('patients.hospital_referral.outbox_receipt_at', 'Kvittering modtaget')}
              value={receiptAt ?? t('patients.hospital_referral.outbox_receipt_at_none', 'Ingen kvittering endnu')}
              muted={!receiptAt}
            />
          </dl>
        </div>
      </div>

      <div className="hv-box">
        <h3>{t('patients.hospital_referral.receipt_history', 'Kvitteringshistorik')}</h3>
        <div className="hv-panel">
          <ReceiptTimeline
            items={history}
            sentAt={sentAt}
            language={i18n.language}
            t={t}
          />
        </div>
      </div>

      {row.companions.length > 0 ? (
        <div className="hv-box">
          <h3>{t('patients.hospital_referral.outbox_companion_receipts', 'Kvittering pr. bilag')}</h3>
          <div className="hv-panel">
            <table className="hv-btbl">
              <thead>
                <tr>
                  <th>{t('patients.hospital_referral.outbox_companion', 'Bilag')}</th>
                  <th>{t('patients.hospital_referral.outbox_status', 'Status')}</th>
                  <th>{t('patients.hospital_referral.outbox_receipt_at', 'Kvittering modtaget')}</th>
                  <th>{t('patients.hospital_referral.outbox_refuse', 'Afvisningskode')}</th>
                </tr>
              </thead>
              <tbody>
                {row.companions.map((companion) => {
                  const receipt = query.data?.companionReceiptByExchangeId[companion.exchangeId];
                  const companionRefuse = knownRefuseCode(receipt?.refuseCode ?? null);
                  return (
                    <tr key={companion.exchangeId}>
                      <td>
                        <span className="fn">
                          <File className="h-4 w-4 text-ds-muted" aria-hidden />
                          {companion.fileName}
                        </span>
                      </td>
                      <td>
                        <ReferralStatusChip status={receipt?.status ?? 'built'} />
                      </td>
                      <td className="num">
                        {formatCourseDateTime(receipt?.receivedAt ?? undefined, i18n.language) ||
                          t('patients.hospital_referral.outbox_receipt_at_none', 'Ingen kvittering endnu')}
                      </td>
                      <td>
                        {companionRefuse
                          ? refuseCodeLabel(companionRefuse, t)
                          : t('patients.hospital_referral.empty', 'Ikke udfyldt')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <OutboxVansPanel clinicId={clinicId} row={row} />
    </Card>
  );
}

function Fact({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={muted ? 'muted' : undefined}>{value}</dd>
    </div>
  );
}

function ReceiptTimeline({
  items,
  sentAt,
  language,
  t,
}: {
  items: readonly ReceiptHistoryItem[];
  sentAt: string | null;
  language: string;
  t: (key: string, fallback: string, opts?: Record<string, unknown>) => string;
}) {
  if (items.length === 0 && !sentAt) {
    return <p className="hv-sub">{t('patients.hospital_referral.receipt_history_empty', 'Ingen kvitteringer endnu.')}</p>;
  }
  return (
    <ol className="hv-tl">
      {items.map((item, index) => {
        const negative = item.status === 'xctl01' || item.status === 'xctl02';
        const refuse = knownRefuseCode(item.refuseCode);
        const at = formatCourseDateTime(item.receivedAt, language) || null;
        return (
          <li
            key={item.id ?? `${item.kind}-${item.receivedAt ?? ''}-${index}`}
            data-receipt-kind={item.kind}
            data-receipt-current={item.current ? 'true' : 'false'}
          >
            <span className={cn('mk', negative ? 'neg' : item.status === 'xctl03' ? 'pos' : undefined)}>
              {negative ? (
                <XCircle className="h-3.5 w-3.5" aria-hidden />
              ) : item.status === 'xctl03' ? (
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
              ) : null}
            </span>
            <div>
              <div className="t">
                {receiptHistoryKindLabel(item, t)}
                {refuse ? ` · ${refuseCodeLabel(refuse, t)}` : ''}
                {item.current ? (
                  <span className="hv-cur">{t('patients.hospital_referral.receipt_history_current', 'Gældende')}</span>
                ) : null}
              </div>
              {at ? <div className="w num">{at}</div> : null}
              {item.refuseText ? <div className="x">{item.refuseText}</div> : null}
            </div>
          </li>
        );
      })}
      {sentAt ? (
        <li>
          <span className="mk">
            <Send className="h-3.5 w-3.5" aria-hidden />
          </span>
          <div>
            <div className="t">{t('patients.hospital_referral.outbox_sent_at', 'Afsendt')}</div>
            <div className="w num">{sentAt}</div>
          </div>
        </li>
      ) : null}
    </ol>
  );
}
